import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const PaymentReceive = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1, totalRows: 0 });

    const [filters, setFilters] = useState({
        username: '', simno: '', senderID: '', seenBy: '', start_date: '', end_date: '', page: 1
    });

    // --- Helper: Cash Out অথবা Received Tk মেসেজ থেকে অ্যামাউন্ট বের করার লজিক ---
    const extractCashOutAmount = (message) => {
        if (!message) return 0;

        // ১. চেক করা হচ্ছে মেসেজে Cash Out অথবা Received Tk আছে কি না
        const isValidType = /Cash[- ]?Out|Received Tk/i.test(message);
        
        if (isValidType) {
            // ২. Tk এর পরের সংখ্যাটি খুঁজে বের করা (কমা থাকলেও সমস্যা নেই)
            // এটি "Tk 2,940.00" বা "Tk 500" সব ফরম্যাট সাপোর্ট করবে
            const match = message.match(/Tk\s?([\d,]+\.?\d*)/i);
            if (match && match[1]) {
                // কমা (,) সরিয়ে ফেলে ফ্লোট নাম্বারে কনভার্ট করা
                return parseFloat(match[1].replace(/,/g, ''));
            }
        }
        return 0; 
    };

    // বর্তমান পেজের মোট অ্যামাউন্ট ক্যালকুলেশন
    const totalCashOutAmount = logs.reduce((sum, log) => sum + extractCashOutAmount(log.message), 0);

    // 🔥 NEW: লোগো খুঁজে বের করার ফাংশন
    const getServiceLogo = (sender) => {
        if (!sender) return '/logo.png';
        const sLower = sender.toLowerCase();
        
        if (sLower.includes('bkash')) return '/bkash.jpg';
        if (sLower.includes('nagad')) return '/nagad.png';
        if (sLower.includes('rocket')) return '/rocket.png';
        if (sLower.includes('whatsapp')) return '/whatsapp.png';
        
        return '/logo.png';
    };

    const fetchData = useCallback(() => {
        setLoading(true);
        const isMaster = localStorage.getItem('isMaster') === 'true';
        let allowedUsers = [];
        try {
            allowedUsers = JSON.parse(localStorage.getItem('allowedUsers') || '[]');
        } catch (e) { allowedUsers = []; }

        socketService.emit('get_payment_logs', { ...filters, isMaster, allowedUsers });
    }, [filters]);

    useEffect(() => {
        socketService.connect();
        socketService.on('receive_payment_logs', (data) => {
            setLogs(data.logs);
            setPagination({ totalPages: data.totalPages, currentPage: data.currentPage, totalRows: data.totalRows });
            setLoading(false);
        });

        socketService.on('new_sms_received', (newLog) => {
            setLogs((prevLogs) => [newLog, ...prevLogs.slice(0, 99)]);
        });

        fetchData();
        return () => {
            socketService.off('receive_payment_logs');
            socketService.off('new_sms_received');
        };
    }, [fetchData]);

    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    const handleReset = () => setFilters({ username: '', simno: '', senderID: '', seenBy: '', start_date: '', end_date: '', page: 1 });
    
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setFilters(prev => ({ ...prev, page: newPage }));
        }
    };

    const getRowClass = (seenBy) => {
        if (!seenBy) return "";
        const status = seenBy.toLowerCase();
        if (status.includes("fake")) return "row-fake";
        if (status.includes("timeout")) return "row-timeout";
        return "";
    };

    const renderPageNumbers = () => {
        const pages = [];
        const { currentPage, totalPages } = pagination;
        pages.push(
            <li key={1} className={`page-item ${currentPage === 1 ? 'active' : ''}`}>
                <button className="page-link shadow-none" onClick={() => handlePageChange(1)}>1</button>
            </li>
        );
        let startPage = Math.max(2, currentPage - 2);
        let endPage = Math.min(totalPages - 1, currentPage + 2);
        if (startPage > 2) pages.push(<li key="dots-s" className="page-item disabled"><span className="page-link border-0">...</span></li>);
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button className="page-link shadow-none" onClick={() => handlePageChange(i)}>{i}</button>
                </li>
            );
        }
        if (endPage < totalPages - 1) pages.push(<li key="dots-e" className="page-item disabled"><span className="page-link border-0">...</span></li>);
        if (totalPages > 1) {
            pages.push(
                <li key={totalPages} className={`page-item ${currentPage === totalPages ? 'active' : ''}`}>
                    <button className="page-link shadow-none" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                </li>
            );
        }
        return pages;
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">📊 Payment Receive Dashboard</h5>
                    <div className="d-flex gap-2">
                        <div className="count-badge">
                            Total Records: <span>{pagination.totalRows}</span>
                        </div>
                        <div className="count-badge" style={{borderColor: '#7c3aed', background: '#f5f3ff'}}>
                            Total Amount: <span style={{color: '#7c3aed'}}>৳{totalCashOutAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                        </div>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="filter-card shadow-sm border-0 mb-4">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-2">
                            <label className="field-label">USERNAME</label>
                            <input type="text" name="username" className="modern-input" placeholder="Search user..." value={filters.username} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">SIM & SENDER</label>
                            <div className="d-flex gap-1">
                                <input type="text" name="simno" className="modern-input w-50" placeholder="SIM" value={filters.simno} onChange={handleFilterChange} />
                                <input type="text" name="senderID" className="modern-input w-50" placeholder="Sender" value={filters.senderID} onChange={handleFilterChange} />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">SEEN BY</label>
                            <input type="text" name="seenBy" className="modern-input" placeholder="Admin name" value={filters.seenBy} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">START DATE</label>
                            <input type="date" name="start_date" className="modern-input" value={filters.start_date} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">END DATE</label>
                            <input type="date" name="end_date" className="modern-input" value={filters.end_date} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2 d-flex gap-2">
                            <button className="btn btn-apply fw-bold w-100" onClick={fetchData}>Apply</button>
                            <button className="btn btn-reset fw-bold w-100" onClick={handleReset}>Reset</button>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-container shadow-sm border-0 mb-3">
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4">USERNAME</th>
                                    <th>SIM & SENDER</th>
                                    <th>AMOUNT (TK)</th>
                                    <th style={{ minWidth: '400px' }}>MESSAGE CONTENT</th>
                                    <th>DATE & TIME</th>
                                    <th className="text-center">STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-5">Loading...</td></tr>
                                ) : logs.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-5 opacity-50">No data found</td></tr>
                                ) : logs.map((log) => {
                                    const cashOutAmt = extractCashOutAmount(log.message);
                                    return (
                                        <tr key={log._id} className={getRowClass(log.seenBy)}>
                                            <td className="ps-4">
                                                <div className="user-primary">{log.userId?.username || log.username || 'N/A'}</div>
                                                <div className="device-secondary text-uppercase">{log.deviceName || 'Unknown'}</div>
                                            </td>
                                            
                                            {/* 🔥 MODIFIED: Logo Added Here */}
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <img 
                                                        src={getServiceLogo(log.senderID)} 
                                                        alt="logo" 
                                                        style={{
                                                            width: '32px', 
                                                            height: '32px', 
                                                            borderRadius: '50%', 
                                                            marginRight: '10px',
                                                            border: '1px solid #e2e8f0',
                                                            padding: '2px',
                                                            objectFit: 'contain',
                                                            background: '#fff'
                                                        }} 
                                                    />
                                                    <div>
                                                        <div className="sim-num">{log.simno}</div>
                                                        <div className="sender-tag">{log.senderID}</div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                {cashOutAmt > 0 ? (
                                                    <div className="fw-bold" style={{color: '#7c3aed', fontSize: '14px'}}>
                                                        ৳{cashOutAmt.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                                                    </div>
                                                ) : (
                                                    <div className="text-muted opacity-25">--</div>
                                                )}
                                            </td>
                                            <td className="msg-body py-3">{log.message}</td>

                                        <td>
                                            {/* এখানে subtract(6, 'hours') যোগ করা হয়েছে যাতে সময় ঠিক দেখায় (আপনার রিকোয়েস্ট অনুযায়ী) */}
                                            <div className="date-bold">
                                                {moment(log.receivedAt).tz('Asia/Dhaka').subtract(6, 'hours').format('DD MMM, YYYY')}
                                            </div>
                                            <div className="time-muted">
                                                {moment(log.receivedAt).tz('Asia/Dhaka').subtract(6, 'hours').format('hh:mm A')}
                                            </div>
                                        </td>

                                            <td className="text-center">
                                                {log.seenBy ? (
                                                    <span className={`badge-status ${log.seenBy.toLowerCase().includes('fake') ? 'bg-fake' : log.seenBy.toLowerCase().includes('timeout') ? 'bg-timeout' : 'bg-seen'}`}>
                                                        {log.seenBy}
                                                    </span>
                                                ) : (
                                                    <span className="badge-unseen">Unseen</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center px-2 py-2">
                    <div className="text-muted small">
                        Showing Page <b>{pagination.currentPage}</b> of <b>{pagination.totalPages}</b>
                    </div>
                    <nav>
                        <ul className="pagination pagination-sm m-0 gap-1">
                            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(pagination.currentPage - 1)}>Previous</button>
                            </li>
                            {renderPageNumbers()}
                            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(pagination.currentPage + 1)}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table tbody tr:hover { background-color: #edf2f7 !important; }
                .row-fake { background-color: #fff1f2 !important; }
                .row-timeout { background-color: #f5f3ff !important; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .device-secondary { font-size: 10px; color: #64748b; font-weight: 800; background: #e2e8f0; display: inline-block; padding: 1px 6px; border-radius: 4px; margin-top: 3px; }
                .sim-num { color: #1e293b; font-weight: 600; font-size: 13px; }
                .sender-tag { color: #6366f1; font-weight: 800; font-size: 11px; }
                .msg-body { color: #334155; font-size: 13px; line-height: 1.5; }
                .date-bold { color: #334155; font-weight: 700; font-size: 13px; }
                .time-muted { font-size: 11px; color: #64748b; }
                .page-link { color: #475569; border: 1px solid #e2e8f0; border-radius: 6px !important; padding: 5px 12px; }
                .page-item.active .page-link { background-color: #0284c7; border-color: #0284c7; color: white; }
                .badge-status { padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid transparent; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
                .bg-timeout { background: #ede9fe; color: #7c3aed; border-color: #ddd6fe; }
                .badge-unseen { background: #f3f4f6; color: #6b7280; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #e5e7eb; }
            `}</style>

        </Layout>
    );
};

export default PaymentReceive;